import { Button, Card, InputNumber, Modal, Space, Tag, Typography } from "antd";
import { saveAs } from "file-saver";
import moment from "moment";
import {
  type ClipboardEvent,
  type KeyboardEvent,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import api from "../../../../api";
import { buildExportFileName, formatAmountInCents } from "../../../../utils";
import DownloadIcon from "../../../../svgs/download.svg?react";
import { ContentWrapper } from "../../../common/styledComponents";
import useEmailParam from "../../../hooks/useEmailParam";
import { ClickableTd, H3, StyledTable } from "../styledComponents";
import SubNavigation, { VAT_TABS } from "../SubNavigation";
import type { IUser } from "@/v1/types";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";
import CustomerSearchInput from "@/lib/customerSearch/CustomerSearchInput";

interface DfvFormRow {
  id: number;
  documentType: "DFV" | "SVZ";
  year: number;
  amount?: number;
  uploadedAt?: string | null;
  createdAt?: string;
  isCorrection?: boolean;
  transmissionMethod?: "AUTOMATIC" | "MANUAL";
  submitterEmail?: string | null;
}

const PDF_LABELS = {
  dfv: "Dauerfristverlaengerung",
  svz: "Sondervorauszahlung",
};

const FORM_LABELS = {
  DFV: "Dauerfristverlaengerung",
  SVZ: "Sondervorauszahlung",
};

const PDF_TYPES = {
  DFV: "dfv",
  SVZ: "svz",
} as const;

const PAST_FORMS_COLUMN_COUNT = 7;

const getPdfFileName = ({
  user,
  email,
  year,
  type,
}: {
  user?: IUser;
  email: string;
  year: number;
  type: "dfv" | "svz";
}) =>
  buildExportFileName({
    user: user ?? { email },
    exportType: PDF_LABELS[type],
    period: { kind: "year", year },
    extension: "pdf",
  });

const downloadPdf = (pdf: string, fileName: string) =>
  saveAs(
    new Blob(
      [Uint8Array.from(atob(pdf), (character) => character.charCodeAt(0))],
      {
        type: "application/pdf",
      }
    ),
    fileName
  );

const parseNonNegativeIntegerInput = (value?: string) =>
  value?.replace(/\D/g, "") ?? "";

const DfvView = () => {
  const { t } = useTranslation("VatView");
  const [emailParam] = useEmailParam();
  const [email, setEmail] = useState(emailParam || "");
  // The customer whose data should be on screen. A slower response for a
  // customer selected earlier must not overwrite it (SMT-1396).
  const shownEmail = useRef(email);
  const [data, setData] = useState<any>();
  const [estimate, setEstimate] = useState<number>();
  const [submitting, setSubmitting] = useState(false);

  const setNonNegativeEstimate = (value: number | string | null) => {
    if (value === null) {
      setEstimate(undefined);
      return;
    }

    const amount = Number(value);
    setEstimate(Number.isFinite(amount) ? Math.max(0, amount) : undefined);
  };

  const preventNonNumericKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key.length === 1 && !/\d/.test(event.key)) {
      event.preventDefault();
    }
  };

  const preventNonNumericPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    if (/\D/.test(event.clipboardData.getData("text"))) {
      event.preventDefault();
    }
  };

  const renderSvzAmountInput = () => (
    <InputNumber
      value={estimate}
      onChange={setNonNegativeEstimate}
      onKeyDown={preventNonNumericKey}
      onPaste={preventNonNumericPaste}
      parser={parseNonNegativeIntegerInput}
      addonBefore={t("dfv.svzInput")}
      controls={false}
      min={0}
      precision={0}
    />
  );

  const reloadAllData = async (forEmail: string = email) => {
    shownEmail.current = forEmail;
    const nextData = await api.kontax.getDfvData(forEmail);
    if (shownEmail.current !== forEmail) {
      return;
    }
    setData(nextData);
    setEstimate(
      nextData.vatPaymentFrequency === "monthly" &&
        typeof nextData.svzAmount === "number"
        ? nextData.svzAmount
        : undefined
    );
  };

  const submit = () => {
    const resubmitSvzOnly = active && isMonthly;
    const year = data?.year || new Date().getFullYear();
    Modal.confirm({
      title: resubmitSvzOnly ? t("dfv.resubmitSvzTitle") : t("dfv.submitTitle"),
      content: resubmitSvzOnly
        ? t("dfv.resubmitSvzContent", { year })
        : t("dfv.submitContent", { year }),
      onOk: async () => {
        setSubmitting(true);
        try {
          const result = resubmitSvzOnly
            ? await api.kontax.submitSvz(email, estimate)
            : await api.kontax.submitDfv(email, estimate);
          const submittedYear = result.year || year;
          if (result.pdfs?.length) {
            result.pdfs.forEach(({ type, pdf }) => {
              if (pdf) {
                downloadPdf(
                  pdf,
                  getPdfFileName({
                    user: data?.user,
                    email,
                    year: submittedYear,
                    type,
                  })
                );
              }
            });
          } else if (result.pdf) {
            downloadPdf(
              result.pdf,
              getPdfFileName({
                user: data?.user,
                email,
                year: submittedYear,
                type: "dfv",
              })
            );
          }
          await reloadAllData();
        } finally {
          setSubmitting(false);
        }
      },
    });
  };

  const frequency = data?.vatPaymentFrequency;
  const submitted = Boolean(data?.dfv);
  const active = submitted || data?.permanentExtensionStatus === "DOES_HAVE";
  const isMonthly = frequency === "monthly";
  return (
    <ContentWrapper>
      <SubNavigation activeTab={VAT_TABS.DFV.key} />
      {/* Resolves Public ID / Account ID to the customer's email and loads
          their DFV data; no unique match shows nothing (SMT-1396). */}
      <CustomerSearchInput
        style={{ width: "min(100%, 420px)" }}
        value={email}
        onSelect={(selected) => {
          setEmail(selected || "");
          if (selected) {
            reloadAllData(selected);
          } else {
            shownEmail.current = "";
            setData(undefined);
          }
        }}
      />
      {data && (
        <Card style={{ marginTop: 16 }} title={t("dfv.title")}>
          <Typography.Paragraph>
            {t("dfv.vatPaymentFrequency", { frequency })}
          </Typography.Paragraph>
          <Typography.Paragraph>
            {t("dfv.permanentExtensionStatus", {
              status: data.permanentExtensionStatus ?? "null",
            })}
          </Typography.Paragraph>
          {active ? (
            <Space direction="vertical">
              <Typography.Text>
                {t("dfv.active", {
                  submittedOn: data.dfv
                    ? t("dfv.submittedOn", { uploadedAt: data.dfv.uploadedAt })
                    : "",
                })}
              </Typography.Text>
              {isMonthly && (
                <>
                  {renderSvzAmountInput()}
                  <Button
                    type="primary"
                    loading={submitting}
                    disabled={estimate === undefined}
                    onClick={submit}
                  >
                    {t("dfv.resubmitSvz")}
                  </Button>
                </>
              )}
            </Space>
          ) : !["monthly", "quarterly"].includes(frequency) ? (
            <Typography.Text>{t("dfv.notAvailable")}</Typography.Text>
          ) : (
            <Space direction="vertical">
              {isMonthly ? (
                renderSvzAmountInput()
              ) : (
                <Typography.Text>
                  {t("dfv.quarterlyNotApplied")}
                </Typography.Text>
              )}
              <Button
                type="primary"
                loading={submitting}
                disabled={isMonthly && estimate === undefined}
                onClick={submit}
              >
                {t("dfv.submit")}
              </Button>
            </Space>
          )}
          <H3 style={{ paddingTop: 24 }}>{t("dfv.pastForms")}</H3>
          <StyledTable>
            <thead>
              <tr>
                <td>{t("dfv.formName")}</td>
                <td>{t("dfv.year")}</td>
                <td>{t("dfv.estimatedAmount")}</td>
                <td>{t("dfv.uploadedOn")}</td>
                <td>{t("dfv.type")}</td>
                <td>{t("dfv.transmission")}</td>
                <td>{t("dfv.form")}</td>
              </tr>
            </thead>
            <tbody>
              {(data.forms || []).length === 0 ? (
                <tr>
                  <td
                    colSpan={PAST_FORMS_COLUMN_COUNT}
                    style={{ textAlign: "center" }}
                  >
                    {t("dfv.noPastForms")}
                  </td>
                </tr>
              ) : (
                (data.forms || []).map((row: DfvFormRow) => (
                  <tr key={row.id}>
                    <td>{FORM_LABELS[row.documentType]}</td>
                    <td>{row.year}</td>
                    <td>
                      {row.documentType === "SVZ"
                        ? formatAmountInCents(row.amount ?? 0, true)
                        : "-"}
                    </td>
                    <td>
                      {row.uploadedAt || row.createdAt
                        ? moment(row.uploadedAt ?? row.createdAt).format(
                            DISPLAY_DATE_FORMAT
                          )
                        : "-"}
                    </td>
                    <td>
                      {row.isCorrection ? (
                        <Tag color="orange">{t("dfv.correction")}</Tag>
                      ) : (
                        <Tag color="blue">{t("dfv.original")}</Tag>
                      )}
                    </td>
                    <td>
                      {row.transmissionMethod === "MANUAL" ? (
                        <span>
                          {t("dfv.manual")}
                          {row.submitterEmail && (
                            <span style={{ color: "#888", marginLeft: 4 }}>
                              ({row.submitterEmail})
                            </span>
                          )}
                        </span>
                      ) : row.transmissionMethod === "AUTOMATIC" ? (
                        t("dfv.automatic")
                      ) : (
                        "-"
                      )}
                    </td>
                    <ClickableTd
                      onClick={async () => {
                        const url = await api.kontax.getDfvPdf(email, row.id);
                        saveAs(
                          url,
                          getPdfFileName({
                            user: data.user,
                            email,
                            year: row.year,
                            type: PDF_TYPES[row.documentType],
                          })
                        );
                      }}
                    >
                      <DownloadIcon />
                      {` ${t("dfv.open")}`}
                    </ClickableTd>
                  </tr>
                ))
              )}
            </tbody>
          </StyledTable>
        </Card>
      )}
    </ContentWrapper>
  );
};

export default DfvView;
